import { Button } from "@jti/core-components/components/Button";
import { checkUrlEquivalence } from "@jti/fe-common/utils";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { useAddProduct } from "@jti/ui/hooks/useAddProduct";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { X_SELLING_POPUP_GTM_CTA_LOCATION } from "@commerce/features/XSellingDialog/const/tracking";

import styles from "./XSellingDialogProductCard.module.scss";

export type XSellingDialogProductCardProps = {
  sku: string;
  name: string;
  description: string;
  productUrl: string;
  imgUrl: string;
  imgAlt: string;
  isSimpleProduct: boolean;
  isInStock: boolean;
  showQuickAddToCartButton?: boolean;
};

export const XSellingDialogProductCard = ({
  sku,
  name,
  description,
  productUrl,
  imgUrl,
  imgAlt,
  isSimpleProduct,
  isInStock,
  showQuickAddToCartButton = false,
}: XSellingDialogProductCardProps) => {
  const { __ } = useTranslations();
  const { isRunning, addProductToCart } = useAddProduct(sku, [], false);

  const discoverButtonLabel = __("x_selling_dialog.pdocut_card.cta_button_label");
  const quickAddToCartButtonLabel = __("product.button.quickaddtocart");

  const isQuickAddToCartEligible = showQuickAddToCartButton && isSimpleProduct;

  const handleDiscoverButtonClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: discoverButtonLabel,
      cta_location: X_SELLING_POPUP_GTM_CTA_LOCATION,
    });

    if (checkUrlEquivalence(window.location.href, productUrl)) {
      window.location.reload();
    } else {
      window.location.assign(productUrl);
    }
  };

  const handleQuickAddToCartButtonClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: quickAddToCartButtonLabel,
      cta_location: X_SELLING_POPUP_GTM_CTA_LOCATION,
    });

    addProductToCart(1);
  };

  return (
    <div className={styles.product}>
      <div className={styles.productDetails}>
        <img className={styles.miniature} src={imgUrl} alt={imgAlt} />
        <div className={styles.productInfo}>
          <h3 className={styles.productName}>{name}</h3>
          <p className={styles.productDescription}>{description}</p>
        </div>
      </div>

      <div className={styles.ctaButtonsGroup}>
        {isQuickAddToCartEligible && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleQuickAddToCartButtonClick}
            disabled={!isInStock}
            pending={isRunning}
          >
            {quickAddToCartButtonLabel}
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={handleDiscoverButtonClick}>
          {discoverButtonLabel}
        </Button>
      </div>
    </div>
  );
};
